import { FC, useState, useCallback } from 'react';
import { Box, Stack, Button, TextField, Typography } from '@mui/material';

import { GroundingExercise } from '../components/GroundingExercise';
import { StepContent } from '../components/StepContent';
import { StepResources } from '../components/StepResources';
import { useStepEntryCrud } from '../hooks/useStepEntryCrud';


type FlowPhase = 'grounding' | 'activation';

interface Props {
  program: Programs.Program;
  step: Programs.ProgramStep;
  initialPhase?: FlowPhase
  entryToday?: Programs.StepEntry
  onComplete: () => void;
}

export const ProgramStepFlow: FC<Props> = ({ program, step, initialPhase, entryToday, onComplete }) => {
  const hasGrounding = !!program.groundingTitle;
  const [phase, setPhase] = useState<FlowPhase>(() => {
    if (initialPhase) {
      return initialPhase;
    }

    return hasGrounding ? 'grounding' : 'activation';
  });
  const [textResponse, setTextResponse] = useState(entryToday ? entryToday.textResponse : '');

  const { createStepEntry, updateStepEntry } = useStepEntryCrud();

  const handleComplete = useCallback(async () => {
    try {
      if (entryToday) {
        await updateStepEntry.mutateAsync({
          id: entryToday.id,
          textResponse,
        });
      } else {
        await createStepEntry.mutateAsync({
          programStep: step.id,
          textResponse,
        });
      }

      onComplete();
    } catch (error: any) {
      if (error?.response?.status === 409) {
        // Same-day restriction — treat as completed
        onComplete();
      } else {
        console.error(error);
      }
    }
  }, [createStepEntry, updateStepEntry, step.id, entryToday, textResponse, onComplete]);

  const hasPrompt = !!step.promptTitle;
  const isCompleteDisabled = hasPrompt && !textResponse.trim();

  const ctaLabel = step.ctaLabel || entryToday ? 'Save' : 'Complete';
  const isPending = createStepEntry.isPending || updateStepEntry.isPending;

  return (
    <>
      {phase === 'grounding' && (
        <GroundingExercise
          title={program.groundingTitle}
          subtitle={program.groundingSubtitle}
          duration={program.groundingDuration}
          onComplete={() => setPhase('activation')}
        />
      )}

      {phase === 'activation' && (
        <Stack spacing={4}>
          <StepContent step={step}/>

          {hasPrompt && (
            <Stack spacing={2}>
              <Typography variant="overline" textAlign="center" color="primary">
                Reflections
              </Typography>
              <TextField
                multiline
                minRows={4}
                maxRows={8}
                placeholder={step.promptTitle}
                value={textResponse}
                onChange={(e) => setTextResponse(e.target.value)}
                fullWidth
              />
            </Stack>
          )}

          <Box textAlign="center">
            <Button
              variant="outlined"
              size="large"
              onClick={handleComplete}
              disabled={isCompleteDisabled || isPending}
            >
              {isPending ? 'Saving...' : ctaLabel}
            </Button>
          </Box>

          <StepResources resources={step.resources}/>
        </Stack>
      )}
    </>
  );
};
